import { Component, Host, Prop, h, Listen, Event, EventEmitter } from '@stencil/core'
import { DetailProduct } from '@rdss/client'
import { select } from '~/selectors'
import {
    getProductCategoryByCharacteristics,
    TrackingDetails,
    TrackingDisturberType,
} from '@rdss/client/dist/catalog/types/tracking'
import { characteristics } from '~/selectors/characteristics'

// Expand ListingProduct to include all fields from the API
type RecoProduct = DetailProduct & {
    manufacturer: string;
    badges: string[];
    media: {
        file: string;
        description: string;
        width: number;
        height: number;
    }[];
};

type group = {
    name: string;
    options: {
        name: string;
    }[];
};

@Component({
    tag: 'wf-product-tile',
    styleUrl: 'wf-product-tile.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfProductTile {
    /**
     * Product.
     */
    @Prop() product: RecoProduct

    /**
     * Image height in px.
     */
    @Prop() imageHeight = 350

    /**
     * Image loading type.
     */
    @Prop() imageLoadingHint: 'lazy' | 'eager' = 'eager'

    /**
     * Show nutritional information link.
     */
    @Prop() showNutritionalInformation = false

    /**
     * Tracking code to append to the pdp link.
     */
    @Prop() pdpTrackingCode?: string = ''

    /**
     * Describes whether the product tile should be condensed.
     */
    @Prop() condense = false

    /**
     * The context (position) of the button used to track add to cart performance.
     */
    @Prop() context = ''

    @Prop() category = ''

    /**
     * Event emitted when the add to cart button is clicked.
     */
    @Event() addToCartTracking: EventEmitter<TrackingDetails>

    /**
     * Extract properties from the product.
     */
    extractProperties(groups) {
        if (!groups) {
            return {}
        }

        const properties = {
            Farbe: null,
            Herkunftsland: null,
            Geschmack: null,
            Weinstil: null,
            Rebsorte: null,
        }

        for (const group of Object.values(groups) as group[]) {
            const { name, options } = group
            if (Object.keys(properties).includes(name) && options.length > 0) {
                properties[name] = options[0].name
            }
        }

        return properties
    }

    /**
     * Add product to cart but prevent going to the product page.
     */
    addToCard(e) {
        e.preventDefault()
    }

    /**
     * Build the individual tracking url by appending the tracking code.
     * @param {string|undefined} url The product url.
     * @param {string} code The tracking code.
     * @returns {string} The product url with tracking code.
     */
    private buildTrackingUrl(url: string|undefined, code: string): string {
        if (!url) return ''

        let trackingUrl = url
        if (code) {
            trackingUrl += (url.indexOf('?') >= 0) ? '&' : '?'
            trackingUrl += 'icid=' + code
        }

        return trackingUrl
    }

    /**
     * Handles the wishlist click.
     */
    @Listen('addToCartTrackingEnrich')
    async handleAddToCartClickedTracking(
        event?: CustomEvent<{ quantity?: number, orderNumber?: string, context?: string, action?: string }>
    ) {
        event.stopPropagation()
        event.stopImmediatePropagation()

        const {
            title,
            manufacturer,
            properties,
            magazineLinks,
            price,
            attributes,
            ordernumber,
        } = this.product

        const characters = characteristics(attributes, properties, manufacturer, magazineLinks, ordernumber)

        document.dispatchEvent(new CustomEvent('addToCartTrackingEnriched', {
            detail: {
                context: event.detail.context,
                products: {
                    [event.detail.orderNumber]: {
                        actionType: event.detail.action !== '' ? event.detail.action : 'not_set',
                        category: getProductCategoryByCharacteristics(characters as [{
                            name: string
                            value: string[]
                        }]),
                        disturberType: price.hasPseudoPrice ? TrackingDisturberType.discount : TrackingDisturberType.none,
                        id: event.detail.orderNumber,
                        name: title,
                        price: price.gross.toString(),
                        totalProductValue: (price.gross * event.detail.quantity).toString(),
                        units: event.detail.quantity.toString(),
                    },
                },
            },
        }));
    }

    /**
     * Render the component.
     */
    render() {
        if (!this.product) return

        const { unit, price, prices } = this.product

        const details = this.product.details[0]

        const media =
            this.product.media && this.product.media.length > 0
                ? this.product.media[0]
                : { file: '', description: '', width: 0, height: 0 }

        const properties = this.extractProperties(this.product.properties?.groups)

        // eslint-disable-next-line prefer-const
        let { awards, badges, characteristics } = select(
            this.product.attributes,
            this.product.properties,
            this.product.price,
            this.product.manufacturer,
            this.product.magazineLinks,
            this.product.ordernumber,
            this.product.nutritionalInformation
        )

        const priceValues = price.pseudoPercent ? [price.pseudoPercent] : []
        const parsedPrice = priceValues.map((pseudoPercent) => {
            const badgeType = {
                type: 'discount',
                class: 'default',
                lines: [],
            }
            badgeType.lines = [`-${Math.round(pseudoPercent)}%`]
            return badgeType
        })

        badges = badges.filter(entry => entry.type !== 'discount')
        if (parsedPrice.length > 0) badges.unshift(parsedPrice[0])

        const year = characteristics?.find(c => c.name === 'Jahrgang')?.value[0] ?? ''

        let productTitle = details?.name
        // if the name is longer than 36 characters, cut it off and add an ellipsis
        if (productTitle?.length > 72) {
            productTitle = productTitle.slice(0, 72) + '...'
        }
        productTitle = `${productTitle.trim()} ${year}`

        return (
            <Host>
                <div class={`wf-product-tile ${this.condense ? 'wf-product-tile-condensed' : ''}`}>
                    <a href={this.buildTrackingUrl(details?.url, this.pdpTrackingCode)}
                        title={details?.name}
                        aria-label={details?.name}
                        role="link">
                        <div class="image-container">
                            <div class={`image ${awards?.length > 0 || badges?.length > 0 ? '' : 'only-image'}`}>
                                {awards?.length > 0 && <wf-awards awards={awards} class="awards"/>}

                                <vx-picture src={media.file} alt={media.description || `${productTitle}`} height="350"/>

                                {badges?.length > 0 && <wf-badges badges={badges} class="badges"/>}
                            </div>
                        </div>
                        <div class="product-info-wrapper">
                            <div class="product-name">{productTitle}</div>
                            <div class="manufacturer-name">{this.product.manufacturer.name}</div>
                            <div class="product-highlights">
                                <ul class="details">
                                    {Object.values(properties)
                                        .slice(0, 3)
                                        .filter(detail => detail)
                                        .map(detail => (
                                            <li class="detail">{detail}</li>
                                        ))}
                                </ul>
                            </div>
                            <div class="product-price">
                                <div>
                                    <wf-price
                                        basePrice={prices[0]?.calculatedReferencePrice}
                                        baseUnit={unit.referenceUnit}
                                        packageSize={unit.purchaseUnit}
                                        unitLong={unit.name}
                                        unitShort={unit.unit}
                                        gross={price.gross}
                                        grossPseudo={price.grossPseudo}
                                        pseudoPercent={price.pseudoPercent}
                                        currency={price.currency}
                                        unitLabelType={unit.unit}
                                        locale="de-DE"
                                        infoUrl="https://www.weinfreunde.de/versand/"
                                        info="inkl. MwSt. zzgl."
                                        minimalLayout={true}
                                    />
                                    {this.showNutritionalInformation && (
                                        <span class="nutrition-information">
                                            <a href={this.product.url + '#tabs-content-1'}>Lebensmittelangaben</a>
                                        </span>
                                    )}
                                </div>
                                <div class="vx-add-to-cart-container" onClick={this.addToCard}>
                                    <vx-add-to-cart
                                        raised={true}
                                        button-height="42px"
                                        button-width="42px"
                                        maxQuantity={1}
                                        disabled={false}
                                        fullwidth={false}
                                        displayQuantitySelect={false}
                                        label={undefined}
                                        icon={'add_shopping_cart'}
                                        orderNumber={this.product.ordernumber}
                                        context={this.context}
                                    />
                                </div>
                            </div>
                        </div>
                    </a>
                </div>
            </Host>
        )
    }
}
